
import React from 'react';
import { createRoot } from 'react-dom/client';
import App from './App.tsx';
import './index.css';

// Determinar se estamos em produção usando consistent logic
const productionDomains = ['primeconveniencia.com.br', 'rspcloud.com.br'];
const isProduction = productionDomains.some(domain => window.location.hostname.includes(domain));
const basePath = isProduction ? '/mobile/' : '/';

console.log('Main.tsx - Inicializando com basePath:', basePath, 'isProduction:', isProduction);

// Função para verificar atualizações do PWA
const checkForUpdates = async () => {
  if ('serviceWorker' in navigator) {
    try {
      // Obter os registros do Service Worker
      const registrations = await navigator.serviceWorker.getRegistrations();
      
      // Atualizar cada Service Worker registrado
      for (const registration of registrations) {
        await registration.update();
      }
      
      console.log('[PWA] Verificação de atualizações executada');
    } catch (error) {
      console.error('[PWA] Erro ao verificar atualizações:', error);
    }
  }
};

// Garantir que toasts e outros componentes de UI não sejam cobertos pela barra de endereço
const setVhProperty = () => {
  const vh = window.innerHeight * 0.01;
  document.documentElement.style.setProperty('--vh', `${vh}px`);
  
  // Adicionar tratamento para tela cheia em diferentes dispositivos
  if (window.matchMedia('(display-mode: standalone)').matches || 
      window.matchMedia('(display-mode: fullscreen)').matches ||
      (navigator as any).standalone === true) {
    document.documentElement.classList.add('standalone');
    document.body.classList.add('standalone');
    
    // Tentar entrar em modo fullscreen quando em Android
    tryEnterFullscreen();
  }
  
  // Log do ambiente atual (útil para debug)
  console.log('Ambiente da aplicação:', {
    isProduction,
    basePath,
    pathname: window.location.pathname,
    hostname: window.location.hostname,
    fullPath: window.location.href
  });
  
  // Garantir que a página possa ser rolada em dispositivos móveis
  document.body.style.overflow = 'auto';
  document.documentElement.style.overflow = 'auto';
  document.documentElement.style.position = 'static';
  
  // Se estamos em produção e não estamos no caminho correto, redirecionar para o caminho base
  if (isProduction) {
    const currentPath = window.location.pathname;
    
    // Redirecionamento especial para o login - se estamos na raiz redirecionar para /mobile/login
    if (currentPath === '/mobile' || currentPath === '/mobile/') {
      // Já estamos no path correto, não precisa redirecionar
      return;
    } else if (currentPath === '/') {
      window.location.href = '/mobile/login';
      return;
    }
  }
  
  // Verificar atualizações quando a página é carregada
  checkForUpdates();
};

// Verificar atualizações periodicamente (a cada 10 minutos)
if ('serviceWorker' in navigator) {
  setInterval(checkForUpdates, 10 * 60 * 1000);
  
  // Verificar se a página está voltando do estado oculto (tabela ou app minimizado)
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') {
      checkForUpdates();
    }
  });
}

// Função para tentar entrar em modo fullscreen em dispositivos compatíveis
const tryEnterFullscreen = () => {
  const isAndroid = /Android/i.test(navigator.userAgent);
  
  if (isAndroid && document.documentElement.requestFullscreen && !document.fullscreenElement) {
    document.documentElement.requestFullscreen()
      .catch(err => console.log('Erro ao entrar em fullscreen:', err));
  }
};

// Configurar a altura da viewport corretamente para dispositivos móveis
window.addEventListener('resize', setVhProperty);
window.addEventListener('orientationchange', setVhProperty);
document.addEventListener('visibilitychange', setVhProperty);

// Inicializar na carga da página
setVhProperty();

// Função para prevenir comportamentos indesejados em dispositivos móveis
const preventDefaultBehaviors = () => {
  // Evitar duplo-toque para zoom em alguns dispositivos
  document.addEventListener('touchstart', (e) => {
    if (e.touches.length > 1) {
      e.preventDefault();
    }
  }, { passive: false });
  
  // Permitir pull-to-refresh apenas no topo da página
  let startY = 0;
  document.body.addEventListener('touchstart', (e) => {
    startY = e.touches[0].clientY;
  }, { passive: true });
  
  document.body.addEventListener('touchmove', (e) => {
    // Permitir rolagem normal na página
  }, { passive: true });
  
  // Tentar entrar em fullscreen ao clicar em qualquer lugar (apenas para Android)
  if (/Android/i.test(navigator.userAgent)) {
    document.addEventListener('click', tryEnterFullscreen, { once: true });
  }
};

// Verificar se está em modo standalone ou fullscreen
if (window.matchMedia('(display-mode: standalone)').matches || 
    window.matchMedia('(display-mode: fullscreen)').matches ||
    (navigator as any).standalone === true ||
    window.location.search.includes('standalone=true')) {
  preventDefaultBehaviors();
}

// Detectar Android e tentar habilitar fullscreen na interação do usuário
if (/Android/i.test(navigator.userAgent)) {
  window.addEventListener('load', () => {
    // Em alguns navegadores Android, o fullscreen só pode ser ativado após interação do usuário
    document.addEventListener('click', tryEnterFullscreen, { once: true });
  });
}

const container = document.getElementById('root');
if (!container) throw new Error('Could not find root element');
const root = createRoot(container);
root.render(
  <React.StrictMode>
    <App />
  </React.StrictMode>
);
